/**
 * The markdown shortcuts a scratchpad actually needs: bold, italic, list
 * continuation and indentation. Nothing else — the panel has no toolbar and the
 * more bindings it grows the less it behaves like a plain text box.
 *
 * This replaces the keymap that `@codemirror/lang-markdown` installs by default
 * (`MarkdownEditor` passes `addKeymap: false`). Two implementations of Enter
 * fighting over the same list item is the kind of bug that only shows up at the
 * edges, so there is exactly one.
 */

import { syntaxTree } from '@codemirror/language'
import {
  EditorSelection,
  type ChangeSpec,
  type EditorState,
  type Line,
  type StateCommand,
} from '@codemirror/state'
import type { KeyBinding } from '@codemirror/view'

/** Jot indents with two spaces, everywhere, and never with tabs. */
const INDENT = '  '

/* --------------------------------------------------------------- list items */

interface ListItem {
  /** The full run of text before the item's content: indent, marker, spacing. */
  prefix: string
  indent: string
  /** `-`, `*` or `+`. Null for an ordered item. */
  bullet: string | null
  /** The number of an ordered item. Null for a bullet. */
  number: number | null
  /** `.` or `)`. Empty for a bullet. */
  delimiter: string
  /** Whitespace between the marker and the content. */
  space: string
  /** Whether the item carries a `[ ]` / `[x]` checkbox. */
  task: boolean
}

const LIST_ITEM = /^([ \t]*)(?:([-*+])|(\d{1,9})([.)]))([ \t]+|$)(\[[ xX]\](?:[ \t]+|$))?/

function parseListItem(text: string): ListItem | null {
  const match = LIST_ITEM.exec(text)
  if (!match) return null
  const digits = match[3]
  return {
    prefix: match[0],
    indent: match[1] ?? '',
    bullet: match[2] ?? null,
    number: digits === undefined ? null : Number(digits),
    delimiter: match[4] ?? '',
    // An item written as bare `-` with no trailing space still counts.
    space: match[5] || ' ',
    task: match[6] !== undefined,
  }
}

/** The marker Enter should open the next item with. */
function nextPrefix(item: ListItem): string {
  const marker = item.bullet ?? `${(item.number ?? 0) + 1}${item.delimiter}`
  // A continued task always starts unchecked, whatever the previous box held.
  return item.indent + marker + item.space + (item.task ? '[ ] ' : '')
}

/**
 * Code blocks are the one place where a line starting with `- ` is not a list
 * and must be left alone.
 */
function inCodeBlock(state: EditorState, pos: number): boolean {
  const innermost = syntaxTree(state).resolveInner(pos, -1)
  for (let node: typeof innermost | null = innermost; node; node = node.parent) {
    if (node.name === 'FencedCode' || node.name === 'CodeBlock') return true
  }
  return false
}

/* ------------------------------------------------------------ bold / italic */

function charAt(state: EditorState, pos: number): string {
  return pos >= 0 && pos < state.doc.length ? state.sliceDoc(pos, pos + 1) : ''
}

/**
 * Whether `text` is delimited by `marker` — and, for a single `*`, that the
 * delimiter is not one half of a `**` pair. Without that check Cmd-I on bold
 * text would quietly demote it to italic instead of toggling anything.
 */
function delimitedBy(text: string, marker: string): boolean {
  const len = marker.length
  if (text.length < len * 2) return false
  if (!text.startsWith(marker) || !text.endsWith(marker)) return false
  return len > 1 || (text[1] !== marker && text[text.length - 2] !== marker)
}

function toggleWrap(marker: string): StateCommand {
  return ({ state, dispatch }) => {
    if (state.readOnly) return false
    const len = marker.length

    const spec = state.changeByRange((range) => {
      const { from, to } = range

      // Case one: the markers sit just outside the selection.
      const outerWrapped =
        from >= len &&
        to + len <= state.doc.length &&
        state.sliceDoc(from - len, from) === marker &&
        state.sliceDoc(to, to + len) === marker &&
        (len > 1 || (charAt(state, from - 2) !== marker && charAt(state, to + 1) !== marker))

      if (outerWrapped) {
        return {
          changes: [
            { from: from - len, to: from },
            { from: to, to: to + len },
          ],
          range: EditorSelection.range(from - len, to - len),
        }
      }

      // Case two: the user selected the markers along with the text.
      if (delimitedBy(state.sliceDoc(from, to), marker)) {
        return {
          changes: [
            { from, to: from + len },
            { from: to - len, to },
          ],
          range: EditorSelection.range(from, to - len * 2),
        }
      }

      // Case three: wrap. On an empty selection this leaves the caret between
      // the two fresh markers, ready to type into.
      return {
        changes: [
          { from, insert: marker },
          { from: to, insert: marker },
        ],
        range: EditorSelection.range(from + len, to + len),
      }
    })

    dispatch(state.update(spec, { scrollIntoView: true, userEvent: 'input' }))
    return true
  }
}

/* ---------------------------------------------------------- enter in a list */

const continueList: StateCommand = ({ state, dispatch }) => {
  if (state.readOnly) return false

  const range = state.selection.main
  // Multiple carets or a live selection: the plain newline is the honest
  // answer, and the default keymap already gives it.
  if (state.selection.ranges.length !== 1 || !range.empty) return false

  const line = state.doc.lineAt(range.head)
  if (inCodeBlock(state, range.head)) return false

  const item = parseListItem(line.text)
  if (!item) return false
  // Caret parked inside the marker itself — splitting the line is what the user
  // asked for, not a new bullet.
  if (range.head < line.from + item.prefix.length) return false

  if (!line.text.slice(item.prefix.length).trim()) {
    // Enter on an empty item ends the list rather than laying down another
    // bullet nobody will fill in.
    dispatch(
      state.update({
        changes: { from: line.from, to: line.to, insert: '' },
        selection: EditorSelection.cursor(line.from),
        scrollIntoView: true,
        userEvent: 'input',
      }),
    )
    return true
  }

  const insert = `\n${nextPrefix(item)}`
  dispatch(
    state.update({
      changes: { from: range.head, insert },
      selection: EditorSelection.cursor(range.head + insert.length),
      scrollIntoView: true,
      userEvent: 'input',
    }),
  )
  return true
}

/* --------------------------------------------------------------- indentation */

/** Every line touched by any selection range, in document order, no repeats. */
function touchedLines(state: EditorState): Line[] {
  const lines: Line[] = []
  for (const range of state.selection.ranges) {
    const first = state.doc.lineAt(range.from)
    const last = state.doc.lineAt(range.to)
    // A selection dragged to the start of the next line does not include it —
    // indenting a line the user cannot see selected is unnerving.
    const end =
      last.number > first.number && last.from === range.to ? last.number - 1 : last.number
    for (let n = first.number; n <= end; n++) {
      const line = state.doc.line(n)
      const previous = lines[lines.length - 1]
      if (!previous || previous.number < line.number) lines.push(line)
    }
  }
  return lines
}

const indentSelection: StateCommand = ({ state, dispatch }) => {
  if (state.readOnly) return false

  const lines = touchedLines(state)
  const only = lines.length === 1 ? lines[0] : undefined
  const single = state.selection.ranges.length === 1 ? state.selection.main : undefined

  // Outside a list Tab is just an indent character. Shifting the whole line
  // would be surprising in prose, but a selection of any kind must not be
  // *replaced* by two spaces, so anything but a bare caret falls through to the
  // line-start shift below.
  if (only && single?.empty === true && !parseListItem(only.text)) {
    dispatch(
      state.update(state.replaceSelection(INDENT), {
        scrollIntoView: true,
        userEvent: 'input',
      }),
    )
    return true
  }

  const changes = state.changes(lines.map((line) => ({ from: line.from, insert: INDENT })))
  dispatch(
    state.update({
      changes,
      // assoc 1 pushes a caret sitting at the line start past the new indent.
      selection: state.selection.map(changes, 1),
      scrollIntoView: true,
      userEvent: 'input.indent',
    }),
  )
  return true
}

const outdentSelection: StateCommand = ({ state, dispatch }) => {
  if (state.readOnly) return false

  const specs: ChangeSpec[] = []
  for (const line of touchedLines(state)) {
    const leading = /^(?:\t| {1,2})/.exec(line.text)
    if (leading) specs.push({ from: line.from, to: line.from + leading[0].length })
  }
  // Nothing to give back: let the key through so Shift-Tab can still walk focus
  // backwards out of the editor.
  if (specs.length === 0) return false

  const changes = state.changes(specs)
  dispatch(
    state.update({
      changes,
      selection: state.selection.map(changes, -1),
      scrollIntoView: true,
      userEvent: 'delete.dedent',
    }),
  )
  return true
}

/* ------------------------------------------------------------------ bindings */

export const markdownKeymap: KeyBinding[] = [
  { key: 'Mod-b', run: toggleWrap('**'), preventDefault: true },
  { key: 'Mod-i', run: toggleWrap('*'), preventDefault: true },
  // Returns false outside a list, which hands Enter back to the default keymap.
  { key: 'Enter', run: continueList },
  { key: 'Tab', run: indentSelection },
  { key: 'Shift-Tab', run: outdentSelection },
]
